import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { Section, SectionHead } from "@/components/landing/Section";
import { StoreLink } from "@/components/landing/StoreLinks";

const PASS_PRICES = {
  epic: { sticker: [99, 299, 599, 999], launch: [50, 150, 300, 500] },
  rare: { sticker: [199, 599, 1199, 1999], launch: [100, 300, 600, 1000] },
};
const TIER_KEYS = ["person", "family", "team", "bus"];

const Perks = ({ list, dark }) => (
  <ul className="space-y-2.5 mt-6">
    {list.map((p) => (
      <li key={p} className={`flex gap-2.5 text-sm ${dark ? "text-parchment/85" : "text-ink"}`}>
        <Check className={`w-4 h-4 mt-0.5 shrink-0 ${dark ? "text-gold" : "text-moss"}`} /> {p}
      </li>
    ))}
  </ul>
);

const PassCard = ({ id, copy, prices, dark, i }) => {
  const { t, fmtNum } = useLang();
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: i * 0.1 }}
      data-testid={`pricing-card-${id}`}
      className={`relative rounded-[20px] p-7 md:p-8 flex flex-col border ${dark ? "bg-ink text-parchment border-ink shadow-journal-lg" : "bg-parchment border-line shadow-journal"}`}
    >
      {copy.ribbon && <span className="stamp absolute -top-4 right-6 rotate-[4deg]">{copy.ribbon}</span>}
      <h3 className="font-display text-2xl font-semibold">{copy.name}</h3>
      <p className={`text-sm mt-1.5 ${dark ? "text-parchment/70" : "text-ink-muted"}`}>{copy.line}</p>
      <p className={`text-xs font-bold mt-1 ${dark ? "text-gold" : "text-amber"}`}>{t.pricing.days}</p>
      <table className="w-full mt-6 text-sm" data-testid={`pricing-table-${id}`}>
        <tbody>
          {TIER_KEYS.map((k, idx) => (
            <tr key={k} className={`border-b border-dashed ${dark ? "border-parchment/15" : "border-line"}`}>
              <td className="py-2.5 font-bold">{t.pricing.tiers[k]}</td>
              <td className={`py-2.5 text-right tabular-nums line-through ${dark ? "text-parchment/45" : "text-ink-muted/70"}`} aria-label={t.pricing.was}>
                {fmtNum(prices.sticker[idx])} kr
              </td>
              <td className="py-2.5 pl-3 text-right tabular-nums font-extrabold text-lg w-24">{fmtNum(prices.launch[idx])} kr</td>
            </tr>
          ))}
        </tbody>
      </table>
      <Perks list={copy.perks} dark={dark} />
      <StoreLink
        testId={`pricing-cta-${id}`}
        className={`mt-8 inline-flex justify-center rounded-full px-6 py-3.5 font-extrabold transition-colors ${dark ? "bg-gold text-ink hover:bg-[#c08b45]" : "bg-amber text-parchment hover:bg-amber-dark"}`}
      >
        {copy.cta}
      </StoreLink>
    </motion.div>
  );
};

export default function Pricing() {
  const { t } = useLang();
  const f = t.pricing.free;
  return (
    <Section id="pricing" testId="pricing-section">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
        <SectionHead kicker={t.pricing.kicker} title={t.pricing.title} />
        <span className="stamp self-start lg:self-auto rotate-[-3deg]" data-testid="pricing-launch-stamp">{t.pricing.launch}</span>
      </div>
      <p className="mt-6 font-bold text-rowan" data-testid="pricing-tagline">{t.tagline}</p>
      <div className="grid lg:grid-cols-3 gap-6 mt-12 items-start">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          data-testid="pricing-card-free"
          className="rounded-[20px] p-7 md:p-8 bg-paper border border-line shadow-journal flex flex-col"
        >
          <h3 className="font-display text-2xl font-semibold">{f.name}</h3>
          <div className="font-display text-6xl font-semibold mt-6 tabular-nums">{f.price}</div>
          <p className="text-lg font-extrabold text-moss mt-3">{f.line}</p>
          <p className="text-sm text-ink-muted mt-1">{f.body}</p>
          <Perks list={f.perks} />
          <StoreLink testId="pricing-cta-free" className="mt-8 inline-flex justify-center rounded-full bg-moss hover:bg-moss-dark text-parchment px-6 py-3.5 font-extrabold transition-colors">
            {f.cta}
          </StoreLink>
        </motion.div>
        <PassCard id="epic" copy={t.pricing.epic} prices={PASS_PRICES.epic} i={1} />
        <PassCard id="rare" copy={t.pricing.rare} prices={PASS_PRICES.rare} dark i={2} />
      </div>
      <p className="text-sm text-ink-muted mt-8">{t.pricing.note}</p>
    </Section>
  );
}
